import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs';

import { EventService } from './event.service';
import { EventPackService } from './event-pack.service';
import { AppEvent } from './models';

/**
 * A preview of the generated event pack, on screen, at roughly the proportions it prints at.
 *
 * Not on the download path - the event detail page renders the document hidden and downloads
 * straight away, because a pack has nothing to configure and a preview between the button and the
 * file is just a step. This exists to be looked at: checking a pack before handing it out, and
 * working on the layout without exporting a PDF each time.
 */
@Component({
    standalone: false,
    selector: 'abs-event-pack',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.PACK.TITLE' | translate">
            @if (event(); as event) {
                <div class="event-pack-actions">
                    <ion-button [disabled]="saving()" (click)="download(event)">
                        @if (saving()) {
                            <ion-spinner name="dots"></ion-spinner>
                        } @else {
                            {{ 'GLOBAL.EVENTS.PACK.DOWNLOAD' | translate }}
                        }
                    </ion-button>
                </div>

                <div class="event-pack event-pack-preview" #packContent>
                    <abs-event-pack-document [event]="event"></abs-event-pack-document>
                </div>
            }
        </abs-page-layout>
    `
})
export class EventPackPage implements OnInit {
    private route = inject(ActivatedRoute);
    private eventService = inject(EventService);
    private packService = inject(EventPackService);

    @ViewChild('packContent') packContent?: ElementRef<HTMLElement>;

    readonly event = signal<AppEvent | null>(null);
    readonly saving = signal(false);

    ngOnInit() {
        this.route.paramMap.pipe(switchMap((params) => this.eventService.getEvent(params.get('eventId')!))).subscribe((event) => {
            this.event.set(event);
        });
    }

    async download(event: AppEvent) {
        const element = this.packContent?.nativeElement;
        if (!element || this.saving()) {
            return;
        }

        this.saving.set(true);
        await this.packService.download(element, event);
        this.saving.set(false);
    }
}
